import _ from 'lodash';
import React from 'react';

import Select, { components, OptionProps } from 'react-select-3';

import {
  LabelChoice,
  isNestedLabelChoice,
  getLabelChoiceValue,
  getLabelChoiceHint,
  getLabelChoiceDisplay,
} from '@scale/shared/types/params/annotation';
import { getDisplayLabel } from '@scale/shared/annotation';
import { AttributeSelectTreeProps } from './AttributeSelectTree';
import HintTooltipIcon from '../../tasks/textcollection/HintTooltipIcon';

type SelectChoice = {
  value: string;
  label: string;
  options?: SelectChoice[];
};

const renderNestedOption = (
  props: OptionProps<SelectChoice>,
  label: string,
  nestedOptions: SelectChoice[],
) => {
  const { innerProps, selectOption } = props;

  return (
    <div>
      <div
        style={{
          color: 'grey',
          paddingLeft: '10px',
        }}
      >
        {label}
      </div>
      {nestedOptions.map((nestedOption: SelectChoice, i: number) => {
        if (nestedOption.options) {
          return renderNestedOption(props, nestedOption.label, nestedOption.options);
        }
        const nestedInnerProps = {
          ...innerProps,
          onClick: () => selectOption(nestedOption),
        };
        const choiceHint = getLabelChoiceHint(nestedOption.value);
        return (
          <components.Option {...props} key={i} innerProps={nestedInnerProps}>
            {nestedOption.label}
            {choiceHint && <HintTooltipIcon key={nestedOption.value} hint={choiceHint} />}
          </components.Option>
        );
      })}
    </div>
  );
};

const Option = (props: OptionProps<SelectChoice>) => {
  const { children, data } = props;
  const nestedOptions = data.options;
  if (nestedOptions) {
    const label = data.label;
    return renderNestedOption(props, label, nestedOptions);
  }
  return <components.Option {...props}>{children}</components.Option>;
};

export const AttributeSearchSelectTree: React.FC<
  AttributeSelectTreeProps & {
    isMulti?: boolean;
    selectedChoices?: string[] | undefined;
    handleMultiSelect?: (choices: string[]) => void;
  }
> = ({
  choices,
  selectedChoice,
  handleSelect,
  isReadOnly,
  isMulti,
  selectedChoices,
  handleMultiSelect,
  description,
}) => {
  const getChoices = (subchoices: LabelChoice[], level: number): SelectChoice[] => {
    return _.map(subchoices, c => {
      const choiceValue = getLabelChoiceValue(c);
      const choiceDisplay = getLabelChoiceDisplay(c);
      if (isNestedLabelChoice(c)) {
        return {
          value: choiceValue,
          label: `${_.times(1 * level, _.constant('—'))} ${choiceDisplay}`,
          options: getChoices(c.subchoices, level + 1),
        };
      } else {
        return {
          value: choiceValue,
          label: `${_.times(1 * level, _.constant('—'))} ${choiceDisplay}`,
        };
      }
    });
  };

  const onChange = (choice: any) => {
    if (isMulti) {
      if (choice && handleMultiSelect) {
        const newChoice = choice.map((c: any) => (c as unknown as SelectChoice).value);
        handleMultiSelect(newChoice);
      } else {
        handleMultiSelect?.([]);
      }
    } else if (!choice) {
      handleSelect(undefined as unknown as string);
    } else {
      handleSelect((choice as unknown as SelectChoice).value);
    }
  };

  const getValue = (choice: string) => ({
    value: getLabelChoiceValue(choice),
    label: getDisplayLabel(choice, choices),
    options: [],
  });
  const value = isMulti
    ? selectedChoices?.map(getValue)
    : selectedChoice
    ? getValue(selectedChoice)
    : undefined;

  return (
    <>
      {description && (
        <div style={{ marginBottom: '12px' }}>
          <span>{description}</span>
        </div>
      )}
      <Select
        isMulti={isMulti}
        menuPortalTarget={document.body}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
        menuPosition={'fixed'}
        components={{
          Option,
        }}
        isClearable
        isSearchable
        formatGroupLabel={choice => {
          return choice.label;
        }}
        formatOptionLabel={choice => {
          return choice.label;
        }}
        getOptionValue={choice => {
          return choice.value;
        }}
        onChange={onChange}
        options={getChoices(choices, 0)}
        placeholder="Select"
        isDisabled={isReadOnly}
        value={value}
      />
    </>
  );
};
